import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import {
  Tab,
  Nav,
  Form,
  Button,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";
import MyContext from "../../MyContext";
import "./AdminDashboard.css";
import { toast, ToastContainer } from "react-toastify";
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import loaderImg from "../../images/Anlook.svg";

/* ---------- tiny reusable ui helpers ---------- */
const Loader = () => (
  <div className="d-flex flex-column justify-content-center align-items-center vh-100">
    <Spinner animation="border" />
    <img src={loaderImg} alt="loading" height={40} className="mt-3" />
  </div>
);

const NoData = ({ text = "No data found." }) => (
  <div className="text-center my-4 fw-semibold text-muted">{text}</div>
);

/* ------------------------------------------------------------------ */
const TabbedPayoutLogs = ({ Admin }) => {
  const AdminParse = typeof Admin === "string" ? JSON.parse(Admin) : Admin;
  const { baseUrl, config } = useContext(MyContext);

  const getToday = () => new Date().toISOString().split("T")[0];
  const getDateDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split("T")[0];
  };

  const [activeTab, setActiveTab] = useState("payout");
  const [startDate, setStartDate] = useState(getDateDaysAgo(7));
  const [endDate, setEndDate] = useState(getToday());
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const twoDecimalPlaces = (value) => {
    return Number(value).toFixed(2).toString();
  };

  // Payout states (kept as-is)
  const [payoutData, setPayoutData] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);

  // Payments states (UPGRADED to infinite scrolling)
  const [paymentData, setPaymentData] = useState([]);      // accumulated rows
  const [loadingPayments, setLoadingPayments] = useState(false); // any page fetch
  const [paymentsInitialLoading, setPaymentsInitialLoading] = useState(false); // first page
  const [paymentsHasMore, setPaymentsHasMore] = useState(true);

  // General
  const [isApplying, setIsApplying] = useState(false);

  // Settlement Config bits (unchanged)
  const [settlementCycle, setSettlementCycle] = useState('daily');
  const [settlementDay, setSettlementDay] = useState('monday');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [loading, setLoading] = useState(false);
  const [SettlementData, setSettlementData] = useState([]);
  const [Clients, setClients] = useState([]);
  const [userID, setuserID] = useState('');
  const [selectedData, setSelectedData] = useState(null);

  // Pagination only for payout table UI (kept)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // --- Payments infinite scroll refs ---
  const paymentsPageIndexRef = useRef(1);     // starts at "1"
  const paymentsPerPageRef = useRef(50);      // fetch 50 each time
  const paymentsFetchingRef = useRef(false);  // hard lock against double calls
  const paymentsLastKeyRef = useRef("");      // guard against stale appends
  const paymentsScrollRef = useRef(null);     // scrollable div ref

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
    }
  }, []);

  useEffect(() => {
    fetchPayouts();
    resetAndLoadPayments(); // start payments infinite load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------- Payouts (unchanged API) --------------------
  const fetchPayouts = async () => {
    try {
      setLoadingPayouts(true);
      const res = await axios.post(
        `${baseUrl}/api/Admin/getAllPayoutList`,
        {
          adminId: AdminParse.pkid,
          userid: "",
          fromDate: startDate,
          toDate: endDate,
        },
        config
      );
      setPayoutData(res.data.responseCode === "00" ? res.data.response.report : []);
    } catch (err) {
      console.error("Error fetching payout report", err);
      toast.error("Failed to fetch payouts");
      setPayoutData([]);
    } finally {
      setLoadingPayouts(false);
      setIsApplying(false);
    }
  };

  // -------------------- Payments (UPGRADED API + infinite scroll) --------------------
  const fetchPaymentsPage = async ({ isFirst = false, contextKey }) => {
    if (paymentsFetchingRef.current || !paymentsHasMore) return;

    paymentsFetchingRef.current = true;
    setLoadingPayments(true);

    try {
      const payload = {
        adnminId: String(AdminParse.pkid),              // NOTE: exact spelling as provided
        userid: String(userID || ""),                   // if you want to filter by a client later
        fromDate: String(startDate),
        toDate: String(endDate),
        pageIndex: String(paymentsPageIndexRef.current),
        recordPerPage: String(paymentsPerPageRef.current),
      };

      const res = await axios.post(
        `${baseUrl}/api/Admin/getTransactionsReport`,
        payload,
        config
      );

      if (res.data.responseCode === "00") {
        const list = Array.isArray(res.data.response) ? res.data.response : [];

        // discard if filters changed mid-flight
        if (contextKey && contextKey !== paymentsLastKeyRef.current) return;

        // append without duplicates
        setPaymentData((prev) => {
          const keyOf = (x) => x.id || x.trx_id || x.transactionId || x.txn_id || x.refrence || x.rrn;
          const seen = new Set(prev.map((x) => keyOf(x)));
          const fresh = list.filter((x) => !seen.has(keyOf(x)));
          return prev.concat(fresh);
        });

        if (list.length < paymentsPerPageRef.current) {
          setPaymentsHasMore(false);
        } else {
          paymentsPageIndexRef.current = paymentsPageIndexRef.current + 1;
        }
      } else {
        if (isFirst) setPaymentData([]);
        setPaymentsHasMore(false);
        toast.error(res.data.responseMessage || "Failed to fetch payments");
      }
    } catch (err) {
      console.error("Error fetching transactions", err);
      if (isFirst) setPaymentData([]);
      setPaymentsHasMore(false);
      toast.error("Failed to fetch payments");
    } finally {
      setLoadingPayments(false);
      paymentsFetchingRef.current = false;
      setIsApplying(false);
    }
  };

  const resetAndLoadPayments = useCallback(async () => {
    const key = `${AdminParse.pkid}|${userID || ""}|${startDate}|${endDate}`;
    paymentsLastKeyRef.current = key;

    // reset
    setPaymentData([]);
    setPaymentsHasMore(true);
    paymentsPageIndexRef.current = 1;
    if (paymentsScrollRef.current) paymentsScrollRef.current.scrollTop = 0;

    setPaymentsInitialLoading(true);
    await fetchPaymentsPage({ isFirst: true, contextKey: key });
    setPaymentsInitialLoading(false);
  }, [AdminParse.pkid, userID, startDate, endDate]);

  // Scroll listener for payments
  const handlePaymentsScroll = useCallback(() => {
    const el = paymentsScrollRef.current;
    if (!el || loadingPayments || !paymentsHasMore) return;

    const threshold = 150; // px from bottom
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    if (nearBottom) {
      const key = paymentsLastKeyRef.current;
      fetchPaymentsPage({ contextKey: key });
    }
  }, [loadingPayments, paymentsHasMore]);

  useEffect(() => {
    const el = paymentsScrollRef.current;
    if (!el) return;
    const onScroll = () => handlePaymentsScroll();
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [handlePaymentsScroll]);

  // -------------------- Filters Apply --------------------
  const handleApplyFilters = () => {
    setIsApplying(true);
    setCurrentPage(1);
    fetchPayouts();
    resetAndLoadPayments(); // reset + first page for payments
  };

  // -------------------- Helpers --------------------
  const filterData = (data) =>
    data
      ?.filter((item) => {
        const matchSearch = Object.values(item).some((val) =>
          val?.toString().toLowerCase().includes(search.toLowerCase())
        );
        const matchStatus =
          !status ||
          item.status === status ||
          item.type === status ||
          item.settlement_status === status;
        return matchSearch && matchStatus;
      })
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // For payments we DON'T slice (infinite scroll)
  const filterPaymentsOnly = (data) =>
    data?.filter((item) => {
      const matchSearch = Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(search.toLowerCase())
      );
      const matchStatus =
        !status ||
        item.status === status ||
        item.type === status ||
        item.settlement_status === status;
      return matchSearch && matchStatus;
    }) || [];

  const renderFilterForm = () => (
    <Form className="mb-3">
      <Row className="align-items-end gy-2">
        <Col md={3}>
          <Form.Label>Start Date</Form.Label>
          <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Col>
        <Col md={3}>
          <Form.Label>End Date</Form.Label>
          <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Col>
        <Col md={2}>
          <Form.Label>Status</Form.Label>
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="Y">Settled</option>
            <option value="N">Unsettled</option>
            <option value="P">Pending</option>
            <option value="1">Credit</option>
            <option value="0">Debit</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label>Search</Form.Label>
          <Form.Control type="text" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Col>
        <Col md={2} className="d-flex align-items-end">
          <Button
            variant="primary"
            className="w-100"
            onClick={handleApplyFilters}
            disabled={loadingPayouts || loadingPayments || isApplying}
          >
            {isApplying ? "Applying..." : "Apply Filters"}
          </Button>
        </Col>
      </Row>
    </Form>
  );

  const getAmountColor = (amount) => {
    if (amount == null) return "";
    const amt = parseFloat(amount);
    return isNaN(amt) ? "" : amt >= 0 ? "text-danger" : "text-success";
  };

  const CompletionBadge = ({ status }) => {
    const isCompleted = status === "Y";
    return (
      <span className={`badge ${isCompleted ? "bg-success" : "bg-danger"}`}>
        {isCompleted ? "Completed" : "Not Completed"}
      </span>
    );
  };

  const SettlementBadge = ({ settlementStatus }) => {
    const isSettled = settlementStatus === "Y";
    return (
      <span className={`badge ${isSettled ? "bg-success" : "bg-warning text-dark"}`}>
        {isSettled ? "Settled" : "Unsettled"}
      </span>
    );
  };

  const CardList = ({ data }) => {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        {data.map((item) => (
          <div
            key={item.id}
            style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '16px',
              width: '250px',
              backgroundColor: '#fff'
            }}
            className="w-100 me-2 h-100 bg-light"
          >
            <h3 style={{ margin: '0 0 8px 0' }}>Settlement Cycle</h3>
            <div className="bg-white p-4 border rounded mb-3">
              <p style={{ margin: '4px 0' }}><strong>Cycle:</strong> {item.settlementCycle}</p>
              <p style={{ margin: '4px 0' }}><strong>Day:</strong> {item.settlementDay}</p>
              <p style={{ margin: '4px 0' }}><strong>Created:</strong> {item.createdOn}</p>
              <p style={{ margin: '4px 0' }}><strong>Updated:</strong> {item.updatedOn}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPagination = (total) => {
    const totalPages = Math.ceil(total / itemsPerPage);
    if (totalPages <= 1) return null;
    return (
      <div className="d-flex justify-content-center mt-3">
        <ul className="pagination">
          {[...Array(totalPages)].map((_, i) => (
            <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                {i + 1}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const PayoutTable = () => {
    if (loadingPayouts) return <Loader />;
    const data = filterData(payoutData);
    if (!data?.length) return <NoData text="No payout records found." />;

    return (
      <div className="scrollable-table border-radius-10-left">
        <table className="table table-bordered table-hover">
          <thead className="text-center">
            <tr>
              <th>#</th>
              <th>Client ID</th>
              <th>Account</th>
              <th>Business</th>
              <th>Client</th>
              <th>Before</th>
              <th>After</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {data.map((item, index) => (
              <tr key={item.id}>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td>{item.us_code || "N/A"}</td>
                <td>{item.account_no || "N/A"}</td>
                <td>{item.business_name || "N/A"}</td>
                <td>{item.client_name || "N/A"}</td>
                <td>₹ {twoDecimalPlaces(item.before_bal)}</td>
                <td>₹ {twoDecimalPlaces(item.after_bal)}</td>
                <td>₹ {twoDecimalPlaces(item.amount)}</td>
                <td>{new Date(item.created_on).toLocaleDateString()}</td>
                <td><CompletionBadge status={item.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {renderPagination(payoutData.length)}
      </div>
    );
  };

  const PaymentTable = () => {
    const data = filterPaymentsOnly(paymentData);
    const showNoData = !paymentsInitialLoading && data.length === 0;

    return (
      <div
        ref={paymentsScrollRef}
        className="scrollable-table border-radius-10-left"
        style={{ maxHeight: "70vh", overflowY: "auto", border: "1px solid #eee", borderRadius: 8 }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead className="text-center sticky-top bg-white">
            <tr>
              <th>S/N</th>
              <th>Date</th>
              <th>Business</th>
              <th>RRN</th>
              <th>Order/Ref ID</th>
              <th>Customer</th>
              <th>Mode</th>
              <th>Credit</th>
              <th>Debit</th>
              <th>Charges</th>
              <th>Net Settlement</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {/* First page placeholder */}
            {paymentsInitialLoading && data.length === 0 && (
              <tr><td colSpan="12" className="text-muted py-4">Loading first page...</td></tr>
            )}

            {/* Rows */}
            {data.length > 0 && data.map((item, idx) => (
              <tr key={item.id || item.trx_id || item.transactionId || item.refrence || `${item.rrn}-${idx}`}>
                <td>{idx + 1}</td>
                <td>{item.datetime || item.date || "N/A"}</td>
                <td>{item.organization_name || item.business_name || "N/A"}</td>
                <td>{item.rrn || "N/A"}</td>
                <td><strong>{item.refrence || item.reference || "N/A"}</strong></td>
                <td>{item.customer_name || "N/A"}</td>
                <td>{item.payment_mode || item.mode || "N/A"}</td>
                <td>{twoDecimalPlaces(item.credit) ?? "N/A"}</td>
                <td>{twoDecimalPlaces(item.debit) ?? "N/A"}</td>
                <td>{twoDecimalPlaces(item.charges) ?? "N/A"}</td>
                <td>{twoDecimalPlaces(item.settlement_amount) ?? "N/A"}</td>
                <td><SettlementBadge settlementStatus={item.settlement_status || item.status || "N/A"} /></td>
              </tr>
            ))}

            {/* No data */}
            {showNoData && (
              <tr><td colSpan="12" className="text-muted py-4">No payment logs found.</td></tr>
            )}

            {/* Loading next pages */}
            {loadingPayments && data.length > 0 && (
              <tr><td colSpan="12" className="text-muted py-3">Loading more...</td></tr>
            )}

            {/* End of results */}
            {!loadingPayments && data.length > 0 && !paymentsHasMore && (
              <tr><td colSpan="12" className="text-muted py-3">End of results.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------- Settlement Configuration (unchanged) ----------------
  const ConfigurationSection = () => {
    const FetchClientCall = async () => {
      setLoading(true);
      try {
        const UserDetail = {
          userid: Admin.pkid,
          latitude: latitude,
          longitude: longitude,
        };

        const response = await axios.post(
          `${baseUrl}/api/Admin/getAllClientList`,
          UserDetail,
          config
        );
        if (response.data.responseCode === "00") {
          setClients(response.data.response);
          setLoading(false);
        } else {
          toast.error(response.data.responseMessage);
        }
      } catch (error) {
        toast.error("error");
        console.error("Error updating Admin login:", error);
      } finally {
        setLoading(false);
      }
    };

    const FetchConfigurationCall = async (user) => {
      setLoading(true);
      if (userID) {
        try {
          const UserDetail = { userid: user };
          const response = await axios.post(
            `${baseUrl}/api/Order/v1.0/getSettlementConfig`,
            UserDetail,
            config
          );
          if (response.data.responseCode === "00") {
            setSettlementData(response.data.response);
          } else {
            toast.error(response.data.responseMessage);
            setSettlementData([]);
          }
        } catch (error) {
          toast.error("error");
          console.error("Error updating Admin login:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    const UpdateConfigurationCall = async () => {
      setLoading(true);
      try {
        const UserDetail = {
          userid: userID,
          adminId: Admin.pkid,
          settlementCycle: settlementCycle,
          settlementDay: settlementDay,
        };

        const response = await axios.post(
          `${baseUrl}/api/Admin/v1.0/updateSettlementConfigurations`,
          UserDetail,
          config
        );
        if (response.data.responseCode === "00") {
          toast.success(response.data.responseMessage);
          FetchConfigurationCall(userID);
        } else {
          toast.error(response.data.responseMessage);
          FetchConfigurationCall(userID);
        }
      } catch (error) {
        toast.error("error");
        console.error("Error updating Admin login:", error);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      FetchClientCall();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
      FetchConfigurationCall(userID);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userID]);

    return (
      <>
        <div className="border bg-light p-2 rounded-3 mb-3">
          <h5 className="mt-1">Select Client To Configure</h5>
          <div>
            <select
              className="form-select form-select-sm"
              aria-label="Small select example"
              onChange={(e) => setuserID(e.target.value)}
            >
              <option value="">Open this select menu</option>
              {Clients.map((item, index) => (
                <option key={index} value={item.us_code}>
                  {item.us_code} : <strong>{item.us_name}</strong>
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="d-flex w-100">
          {SettlementData && (
            <div className="w-100">
              <CardList data={SettlementData} />
            </div>
          )}

          {userID && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                UpdateConfigurationCall();
              }}
              className="w-100"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                width: '100%',
                padding: '20px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <label>
                Settlement Cycle:
                <select
                  value={settlementCycle}
                  onChange={(e) => setSettlementCycle(e.target.value)}
                  style={{ padding: '8px', marginTop: '4px', width: '100%' }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>

              <label>
                Settlement Day:
                <select
                  value={settlementDay}
                  onChange={(e) => setSettlementDay(e.target.value)}
                  style={{ padding: '8px', marginTop: '4px', width: '100%' }}
                >
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </select>
              </label>

              <button
                type="submit"
                style={{
                  padding: '10px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginTop: '12px'
                }}
              >
                Update Confriguration
              </button>
            </form>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="mt-4 marginRight">
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="mx-4">
        <div className="rounded-2 border p-2 admin-green-gradient mb-2 admin-popup">
          <h3 className="mb-4 text-center">Payout & Payment Logs</h3>
          {renderFilterForm()}
        </div>

        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => {
            setCurrentPage(1);
            setActiveTab(k);
          }}
        >
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="payout">Payout Report</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="payment">Payment Logs</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="configurations">Settlement Config</Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="payout">{PayoutTable()}</Tab.Pane>
            <Tab.Pane eventKey="payment">{PaymentTable()}</Tab.Pane>
            <Tab.Pane eventKey="configurations">
              {ConfigurationSection()}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

export default TabbedPayoutLogs;
